import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/great-decoupling-colorado";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/great-decoupling-colorado")({
  head: () => ({
    meta: [
        { title: "Why Your Google Traffic Is Dropping in Colorado" },
      { title: "Why Your Google Traffic Is Dropping Even Though You're Ranking Higher" },
      { name: "description", content: "The 'Great Decoupling': why Colorado businesses see Google traffic drop even when rankings hold, and what AI Overviews are doing to local search." },
      { property: "og:title", content: "Why Your Google Traffic Is Dropping in Colorado" },
      { property: "og:description", content: "The 'Great Decoupling': why Colorado businesses see Google traffic drop even when rankings hold, and what AI Overviews are doing to local search." },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "The Great Decoupling for Colorado Businesses",
        description: ARTICLE?.desc ?? "AI Overviews are eating your clicks. Here's what to do.",
      }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="AI & GEO"
      read="8 min read"
      title="Why your Google traffic is dropping even though you're ranking higher"
      intro="If your Search Console looks like impressions are climbing but clicks are falling — you're not imagining it. It has a name. And it's quietly draining leads from Colorado small businesses right now."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "The Great Decoupling" },
      ]}
      path={PATH}
      faqs={[
        { q: "What is The Great Decoupling?", a: "It's the trend where Google impressions keep rising while clicks fall — because AI Overviews and other zero-click features answer the searcher's question directly on the results page, so nobody clicks through to the site that's 'ranking.'" },
        { q: "How big is the impact for local Colorado businesses?", a: "Industry data shows AI Overviews can reduce clicks to top-ranked pages by 30%+ on informational queries. Local 'near me' and emergency-service queries are less affected because the map pack still drives the call — but informational ('how much does X cost in Colorado') is getting hit hard." },
        { q: "Should I stop tracking rankings?", a: "No — but stop using rank as your primary KPI. Track AI citation share, branded search volume, and direct/organic conversions. Position #1 in 2026 doesn't mean what it meant in 2022." },
      ]}
    >
      <h2>What's actually happening</h2>
      <p>
        I'll give you the version I give my clients in person. You used to be able to look at Google Search Console, see your impressions
        going up and your average position improving, and assume more leads were coming. That equation is broken now.
      </p>
      <p>
        Here's why: when Google shows an AI Overview at the top of the results page, it's pulling the answer from your content (and a few
        competitors), summarizing it, and showing it directly to the searcher. The searcher reads the answer, doesn't click anything, and
        moves on. You got the impression. You got "credit" for ranking. You got zero clicks and zero leads.
      </p>
      <p>The industry calls this <strong>The Great Decoupling</strong>: the link between visibility and clicks has broken.</p>

      <h2>What it looks like in your Search Console</h2>
      <ul>
        <li>Impressions: up 20–60% year over year</li>
        <li>Average position: improving (e.g. from 7.2 to 4.8)</li>
        <li>Clicks: <em>flat or down</em></li>
        <li>CTR: dropping noticeably on informational queries</li>
      </ul>
      <p>
        If that pattern looks familiar, you're not under-performing. You're being summarized. The page above your link is using your
        content to answer the question without the click.
      </p>

      <h2>What it does NOT mean</h2>
      <ul>
        <li><strong>SEO is not dead.</strong> Local intent queries ("emergency plumber Colorado Springs") still drive calls through the map pack.</li>
        <li><strong>Your rankings still matter.</strong> AI Overviews pull from highly-ranked, well-structured pages. If you're not ranking, you're not getting cited.</li>
        <li><strong>It's not your fault.</strong> Nobody did anything wrong. The platform changed.</li>
      </ul>

      <h2>What to look at instead</h2>
      <p>For Colorado small businesses, here's the honest list of metrics that actually matter in 2026:</p>
      <ol>
        <li><strong>AI citation share.</strong> Run 10–25 real customer questions in ChatGPT, Perplexity, Gemini, and Google AI Overviews. Are you getting named? How often? In which categories? Track this monthly.</li>
        <li><strong>Branded search volume.</strong> Are more people Googling your business name? That's the cleanest signal that AI tools are recommending you.</li>
        <li><strong>Direct and organic <em>conversions</em>, not just sessions.</strong> Booked jobs, form fills, calls. Traffic vanity metrics lie. Conversions don't.</li>
        <li><strong>Google Business Profile insights.</strong> Calls, direction requests, and website clicks from GBP are still the cleanest local-intent signal.</li>
      </ol>

      <h2>What to actually do about it</h2>
      <ol>
        <li><strong>Make your pages quotable.</strong> AI Overviews pull short, direct, structured answers. Add a one-paragraph "What is X?" near the top of every service page.</li>
        <li><strong>Add FAQ schema everywhere.</strong> Real questions, real answers. Both Google and the LLMs love it.</li>
        <li><strong>Earn local citations.</strong> Colorado Springs Gazette, BBB, the Chamber, industry directories. AI tools weight authoritative mentions heavily.</li>
        <li><strong>Keep your Google Business Profile spotless.</strong> It's a primary source for AI citations, not just the map pack.</li>
        <li><strong>Stop chasing thin keyword pages.</strong> Depth and authority beat volume now. One great page beats ten mediocre ones.</li>
      </ol>

      <h2>The honest bottom line</h2>
      <p>
        If your traffic has been quietly dropping and your agency keeps showing you "look, your rankings are great!" reports — you need
        to look deeper. Rankings without clicks are vanity. The work has shifted from "rank for the keyword" to "be the source the AI
        cites." That's a different game, and most Colorado business owners don't realize it's already underway.
      </p>
      <p>
        That's exactly what my{" "}
        <Link to="/ai-visibility-audit" className="font-bold text-brand-gold-deep underline">
          AI Visibility Audit
        </Link>{" "}
        is for: I run real customer prompts in the AI tools, show you exactly where you stand, and give you a 90-day roadmap. Or read
        the foundational explainer:{" "}
        <Link to="/learn/ai-search-geo" className="font-bold text-brand-gold-deep underline">
          AI search & GEO for Colorado small businesses
        </Link>
        .
      </p>
    </ArticleLayout>
  );
}
